import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  Home, 
  Search, 
  Globe,
  Shield,
  ArrowRight,
  CheckCircle
} from '@/lib/icons';
import { Link } from 'react-router-dom';

interface SubdomainNotFoundProps {
  subdomain?: string;
  suggestedSubdomains?: string[];
}

export const SubdomainNotFound = ({ subdomain, suggestedSubdomains = [] }: SubdomainNotFoundProps) => {
  const mainDomain = import.meta.env.VITE_MAIN_DOMAIN || 'imobpanda.site';
  const currentSubdomain = subdomain || window.location.hostname.split('.')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Subdomínio não encontrado
          </h1>
          <p className="text-gray-600">
            O subdomínio <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
              {currentSubdomain}.{mainDomain}
            </code> não existe ou não está disponível.
          </p>
        </div>

        {/* SSL Security Notice */}
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <Shield className="w-5 h-5" />
              Aviso de Segurança
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="text-sm text-orange-700">
                  <strong>Este site é seguro!</strong> O certificado SSL está funcionando corretamente.
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  O aviso "não seguro" aparece porque o subdomínio não existe, mas a conexão é criptografada.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              O que você pode fazer?
            </CardTitle>
            <CardDescription>
              Escolha uma das opções abaixo para continuar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Option 1: Go to main site */}
            <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Home className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-medium">Acessar site principal</h3>
                    <p className="text-sm text-gray-600">
                      Vá para a página inicial do ImobPanda
                    </p>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/">
                    Acessar
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Option 2: Search for correct subdomain */}
            {suggestedSubdomains.length > 0 && (
              <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-green-600" />
                    <div>
                      <h3 className="font-medium">Subdomínios similares encontrados</h3>
                      <p className="text-sm text-gray-600">
                        Talvez você esteja procurando por um destes:
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {suggestedSubdomains.map((suggestion, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        asChild
                        className="text-xs"
                      >
                        <a 
                          href={`https://${suggestion}.${mainDomain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {suggestion}.{mainDomain}
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Option 3: Create your own subdomain */}
            <div className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <div>
                    <h3 className="font-medium">Criar seu próprio subdomínio</h3>
                    <p className="text-sm text-gray-600">
                      Registre-se e crie seu marketplace personalizado
                    </p>
                  </div>
                </div>
                <Button asChild variant="default">
                  <Link to="/auth">
                    Criar Conta
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Info */}
        <Card className="bg-gray-50">
          <CardContent className="pt-6">
            <div className="text-center space-y-2">
              <Badge variant="outline" className="text-xs">
                Informação Técnica
              </Badge>
              <p className="text-xs text-gray-500">
                Subdomínios válidos têm o formato: <code>nome.imobpanda.site</code>
              </p>
              <p className="text-xs text-gray-500">
                Certificado SSL válido para *.imobpanda.site
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
